import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { useGetLoanDisbursementHistory } from '../../../services/loanService';
import { format } from 'date-fns';

interface DisbursementItem {
    id: string;
    applicationId: string;
    applicationNumber: string;
    customerName: string;
    customerBusinessName?: string;
    bankName?: string;
    accountNumber?: string;
    disbursementAmount?: number;
    bankReference?: string;
    disbursedBy: string;
    disbursementDate: string;
    processedDate: string;
    createdDate: string;
}

const Disbursements = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const ALL_DISBURSEMENTS_PAGE_SIZE = 10000;

    // Fetch disbursement history
    const {
        data: disbursementHistory,
        isLoading,
        error
    } = useGetLoanDisbursementHistory({
        page: 0,
        size: ALL_DISBURSEMENTS_PAGE_SIZE
    });

    const columns = [
        {
            name: 'Application #',
            selector: (row: DisbursementItem) => row.applicationNumber,
            sortable: true,
            cell: (row: DisbursementItem) => (
                <Link to={`/disbursement/${row.applicationId}`} className="text-primary">
                    {row.applicationNumber}
                </Link>
            ),
        },
        {
            name: 'Customer',
            selector: (row: DisbursementItem) => row.customerBusinessName || row.customerName,
            sortable: true,
            cell: (row: DisbursementItem) => (
                <div>
                    {row.customerBusinessName ? (
                        <>
                            <div>{row.customerBusinessName}</div>
                            <small className="text-muted">{row.customerName}</small>
                        </>
                    ) : (
                        row.customerName
                    )}
                </div>
            ),
        },
        {
            name: 'Bank Details',
            cell: (row: DisbursementItem) => (
                <div>
                    {row.bankName && <div>{row.bankName}</div>}
                    {row.accountNumber && <small className="text-muted">{row.accountNumber}</small>}
                </div>
            ),
        },
        {
            name: 'Amount',
            selector: (row: DisbursementItem) => row.disbursementAmount || 0,
            cell: (row: DisbursementItem) => (
                row.disbursementAmount?.toLocaleString('en-ZA', {
                    style: 'currency',
                    currency: 'ZAR'
                }) || '-'
            ),
            sortable: true,
            right: true,
        },
        {
            name: 'Reference',
            selector: (row: DisbursementItem) => row.bankReference || '-',
            sortable: true,
        },
        {
            name: 'Disbursed By',
            selector: (row: DisbursementItem) => row.disbursedBy,
            sortable: true,
        },
        {
            name: 'Disbursement Date',
            selector: (row: DisbursementItem) => row.disbursementDate,
            cell: (row: DisbursementItem) => format(new Date(row.disbursementDate), 'dd MMM yyyy HH:mm'),
            sortable: true,
        },
        {
            name: 'Processed Date',
            selector: (row: DisbursementItem) => row.processedDate,
            cell: (row: DisbursementItem) => format(new Date(row.processedDate), 'dd MMM yyyy HH:mm'),
            sortable: true,
        }
    ];

    // Calculate statistics from the history data
    const stats = [
        {
            title: 'Total Disbursements',
            value: (disbursementHistory?.content?.length || 0).toString(),
            icon: 'fa-money',
            color: 'primary'
        },
        {
            title: 'Total Amount',
            value: disbursementHistory?.content?.reduce((sum, item) => sum + (item.disbursementAmount || 0), 0).toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR'
            }) || 'R 0',
            icon: 'fa-wallet',
            color: 'success'
        },
        {
            title: 'Today',
            value: disbursementHistory?.content?.filter(item => {
                if (!item.disbursementDate) return false;
                const today = new Date();
                const disbursementDate = new Date(item.disbursementDate);
                return disbursementDate.toDateString() === today.toDateString();
            }).length.toString() || '0',
            icon: 'fa-calendar-day',
            color: 'info'
        },
        {
            title: 'This Month',
            value: disbursementHistory?.content?.filter(item => {
                if (!item.disbursementDate) return false;
                const today = new Date();
                const disbursementDate = new Date(item.disbursementDate);
                const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
                return disbursementDate >= monthAgo;
            }).length.toString() || '0',
            icon: 'fa-calendar-alt',
            color: 'warning'
        }
    ];

    const handlePageChange = (page: number) => {
        setPage(page);
    };

    const handlePerRowsChange = (newSize: number, page: number) => {
        setSize(newSize);
        setPage(page);
    };

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Disbursements</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    {/* Stats Cards */}
                    <div className="row g-3 mb-3 row-deck">
                        {stats.map((stat, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <i className={`fa ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                                        <div className="mb-2 text-uppercase">{stat.title}</div>
                                        <div><span className="h4">{stat.value}</span></div>
                                        <small className="text-muted">Last 24 hours</small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Main Content */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h6 className="card-title mb-0">Disbursement Queue</h6>
                                </div>
                                <div className="card-body">
                                    {error ? (
                                        <div className="alert alert-danger">
                                            Failed to load disbursement history: {(error as Error).message}
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={(disbursementHistory?.content || []) as DisbursementItem[]}
                                                progressPending={isLoading}
                                                pagination
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}
                                                paginationPerPage={size}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                highlightOnHover
                                                striped
                                                responsive
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Disbursements; 
